// ABOUTME: The hooks the feed screens render from: whether there is a feed for this talent at all, the paged feed, one
// ABOUTME: listing with its two not-found states kept apart, and the saved searches and points with the three-point rule.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UNNAMED_ERROR_CODE, useTrackerArm } from 'modules/applications';
import { FeatureFlag, useFeatureToggle } from 'modules/unleash';
import { ApiError } from 'utils/api';
import { COMMUTE_SERVED } from './client';
import {
  JOB_LISTING_NOT_FOUND_ERROR_CODE,
  JOB_LISTINGS_UNAVAILABLE_ERROR_CODE,
  MAX_SAVED_POINTS
} from './constants';
import {
  ApplyExtras,
  SearchPreferencesUpdate,
  useApplyToJobListingMutation,
  useCommuteQuery,
  useFilterOptionsQuery,
  useJobListingQuery,
  useJobListingsQuery,
  useJobPageQuery,
  useProCvQuery,
  useRecordEngagementMutation,
  useSaveSearchPreferencesMutation,
  useSearchPreferencesQuery,
  useWeekFitQuery
} from './query';
import {
  ApplyAnswer,
  FilterOptions,
  JobListing,
  JobListingCard,
  JobListingSearchCriteria,
  JobPage,
  ListingEngagementAction,
  ListingEngagementSource,
  SavedPoint,
  SavedSearch,
  SearchPreferences,
  WeekFitAnswer,
  WeekFitRequest
} from './types';

const codeOf = (error: unknown): string | null =>
  error instanceof ApiError ? error.code : null;

const isCode = (error: unknown, code: string): boolean =>
  codeOf(error) === code;

/**
 * Whether this talent has a feed at all, in the one place that decides it. PRO_JOB_LISTING is a switch: pro-core
 * reads `isEnabled` for the same talent, so both sides answer the same question the same way.
 */
export const useJobFeedEnabled = (): {
  isEnabled: boolean;
  isLoading: boolean;
} => {
  const { isEnabled, isLoading } = useFeatureToggle(
    FeatureFlag.PRO_JOB_LISTING
  );
  return { isEnabled, isLoading };
};

export interface JobFeedState {
  /** Every card loaded so far, in the order the backend sends them; undefined until the first page is there. */
  listings: JobListingCard[] | undefined;
  /** How many listings the whole search holds, from the last page's meta. */
  total: number;
  hasMore: boolean;
  loadMore: () => void;
  isLoadingMore: boolean;
  isLoading: boolean;
  /** A genuine failure. Never true for the off state below. */
  isError: boolean;
  /** PRO_JOB_LISTING is off for this talent: the friendly off state, not an error. */
  isUnavailable: boolean;
  error: unknown;
}

/** The feed for one set of criteria. One page on mount; the next only when the talent asks for it. */
export const useJobFeed = (
  criteria: JobListingSearchCriteria
): JobFeedState => {
  const {
    data,
    isIdle,
    isLoading,
    isError,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useJobListingsQuery(criteria);

  const listings = useMemo(
    () => data?.pages.flatMap(page => page.data),
    [data]
  );
  const lastPage = data?.pages[data.pages.length - 1];
  const isUnavailable =
    isError && isCode(error, JOB_LISTINGS_UNAVAILABLE_ERROR_CODE);

  return {
    listings,
    total: lastPage?.meta.pagination.totalElements ?? 0,
    hasMore: !!hasNextPage,
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
    },
    isLoadingMore: isFetchingNextPage,
    // Idle means the query is waiting for the Auth0 token: still loading as far as the screen is concerned.
    isLoading: isLoading || isIdle,
    isError: isError && !isUnavailable,
    isUnavailable,
    error
  };
};

export interface JobListingState {
  listing: JobListing | undefined;
  isLoading: boolean;
  isError: boolean;
  isUnavailable: boolean;
  /** The listing was paused or closed while the talent was reading, or never was theirs to see. */
  isNotFound: boolean;
  error: unknown;
}

/** One listing, with "this job is no longer available" kept apart from "the feed is off" and from a genuine failure. */
export const useJobListing = (refId: string | undefined): JobListingState => {
  const { data, isIdle, isLoading, isError, error } = useJobListingQuery(refId);
  const isUnavailable =
    isError && isCode(error, JOB_LISTINGS_UNAVAILABLE_ERROR_CODE);
  const isNotFound = isError && isCode(error, JOB_LISTING_NOT_FOUND_ERROR_CODE);

  return {
    listing: data,
    isLoading: isLoading || isIdle,
    isError: isError && !isUnavailable && !isNotFound,
    isUnavailable,
    isNotFound,
    error
  };
};

export interface FilterOptionsState extends FilterOptions {
  /** Whether the options arrived. Until they do, and when they never do, the sheet offers neither filter. */
  isLoaded: boolean;
}

const NO_OPTIONS: FilterOptions = { occupations: [], companies: [] };

/**
 * What the occupation and company filters can pick from. A failure is not the sheet's problem: it reads as no options,
 * so the two filters are simply not offered, which is also what a pro-core without the route answers.
 */
export const useFilterOptions = (): FilterOptionsState => {
  const { data, isSuccess } = useFilterOptionsQuery();
  return { ...(data ?? NO_OPTIONS), isLoaded: isSuccess };
};

export interface SearchPreferencesState {
  savedSearches: SavedSearch[];
  savedPoints: SavedPoint[];
  isLoading: boolean;
  /** Whether the stored document was read; until then nothing may be saved, since a save replaces it whole. */
  isLoaded: boolean;
  isSaving: boolean;
  canAddPoint: boolean;
  /** Replaces the whole document. */
  save: (preferences: SearchPreferences) => Promise<SearchPreferences>;
  /** Adds one point, refusing a fourth before any request is made. */
  addPoint: (point: SavedPoint) => Promise<SearchPreferences>;
  /** Adds or replaces one saved search by label. */
  saveSearch: (search: SavedSearch) => Promise<SearchPreferences>;
}

const EMPTY: SearchPreferences = { savedSearches: [], savedPoints: [] };

export const useSearchPreferences = (): SearchPreferencesState => {
  const { data, isIdle, isLoading, isSuccess } = useSearchPreferencesQuery();
  const mutation = useSaveSearchPreferencesMutation();
  const { mutateAsync } = mutation;
  // Every save replaces the document whole, so two in flight would each drop what the other added. They run one
  // after another, and each builds its document from what the one before it stored, not from this render's copy.
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const preferences = data ?? EMPTY;
  const canAddPoint = preferences.savedPoints.length < MAX_SAVED_POINTS;

  const enqueue = (update: SearchPreferencesUpdate) => {
    const run = queue.current.then(() => mutateAsync(update));
    queue.current = run.catch(() => undefined);
    return run;
  };

  return {
    savedSearches: preferences.savedSearches,
    savedPoints: preferences.savedPoints,
    isLoading: isLoading || isIdle,
    isLoaded: isSuccess,
    isSaving: mutation.isLoading,
    canAddPoint,
    save: next => enqueue(() => next),
    addPoint: point => {
      if (!canAddPoint) {
        return Promise.reject(
          new Error(`at most ${MAX_SAVED_POINTS} saved points`)
        );
      }
      return enqueue(current => {
        // Checked again on the stored document: a place saved just before this one may have used the last slot.
        if (current.savedPoints.length >= MAX_SAVED_POINTS) {
          throw new Error(`at most ${MAX_SAVED_POINTS} saved points`);
        }
        return { ...current, savedPoints: [...current.savedPoints, point] };
      });
    },
    saveSearch: search =>
      enqueue(current => ({
        ...current,
        savedSearches: [
          ...current.savedSearches.filter(item => item.label !== search.label),
          search
        ]
      }))
  };
};

/**
 * Whether this talent may apply from the app, in one place so the button and the copy beneath it cannot disagree:
 * the listing surface (PRO_JOB_LISTING) and the engine behind it (the PRO_ATS tracker arm) both have to be on, which
 * is exactly the pair of gates pro-core checks before it takes an application.
 */
export const useCanApply = (): { canApply: boolean; isLoading: boolean } => {
  const listing = useFeatureToggle(FeatureFlag.PRO_JOB_LISTING);
  const arm = useTrackerArm();
  return {
    canApply: listing.isEnabled && arm.isTracker,
    isLoading: listing.isLoading || arm.isLoading
  };
};

/** Whether this talent has a PRO CV, for the line the apply footer puts above the button. */
export const useHasProCv = (): { hasCv: boolean; isLoading: boolean } => {
  const { data, isIdle, isLoading } = useProCvQuery();
  return { hasCv: !!data, isLoading: isLoading || isIdle };
};

/** PJL: the Jobs page as one feed of sections, the new card and details parts, the funnel steps. */
export const useListingFeedEnabled = (): {
  isEnabled: boolean;
  isLoading: boolean;
} => useFeatureToggle(FeatureFlag.PRO_LISTING_FEED);

/** PJL: applying in three steps with the week, and the tracker's additions. */
export const useApplyV2Enabled = (): {
  isEnabled: boolean;
  isLoading: boolean;
} => useFeatureToggle(FeatureFlag.PRO_APPLY_V2);

/**
 * The job page of an application or of my job: `ready` with the job, `loading` while it is asked, `none` when there is
 * no job page to show. pro-core serves the job only with PRO_JOB_LISTING and PRO_APPLY_V2 on for the talent, so the
 * app asks only then; off, a 404 or any failure is `none`, and the page stays the one it was.
 */
export type JobPageState =
  | { state: 'none' }
  | { state: 'loading' }
  | { state: 'ready'; job: JobPage };

export const useJobPage = (
  of: 'application' | 'assignment',
  ref: string | undefined
): JobPageState => {
  const { isEnabled: jobListing, isLoading: jobListingLoading } =
    useJobFeedEnabled();
  const { isEnabled: applyV2, isLoading: applyV2Loading } = useApplyV2Enabled();
  const asked = jobListing && applyV2 ? ref : undefined;
  const { data, isIdle, isLoading, isError } = useJobPageQuery(of, asked);
  if (ref && (jobListingLoading || applyV2Loading)) return { state: 'loading' };
  if (!asked) return { state: 'none' };
  // A failed refetch keeps the cached job next to the error: the error wins, as on the job details page.
  if (isError) return { state: 'none' };
  if (data) return { state: 'ready', job: data };
  if (isLoading || isIdle) return { state: 'loading' };
  return { state: 'none' };
};

export interface CommuteState {
  /** Whether the commute can be asked at all: pro-core does not serve it yet (PJL-056), only the mock does. */
  available: boolean;
  /** Ask for the minutes; nothing is asked before this, since every answer costs a routing call. */
  ask: () => void;
  asked: boolean;
  /** Minutes per place refId, once they arrived. */
  minutes: Record<string, number> | undefined;
  isLoading: boolean;
  isError: boolean;
}

/** The travel time to a listing's places by public transport, asked on the talent's tap and kept for the visit. */
export const useCommute = (refId: string): CommuteState => {
  const [asked, setAsked] = useState(false);
  const { data, isLoading, isError } = useCommuteQuery(refId, 'TRANSIT', asked);
  const minutes = useMemo(
    () =>
      data
        ? Object.fromEntries(
            data.locations.map(place => [place.refId, place.minutes])
          )
        : undefined,
    [data]
  );
  return {
    available: COMMUTE_SERVED,
    ask: () => setAsked(true),
    asked,
    minutes,
    isLoading: asked && isLoading,
    isError
  };
};

/** Listings whose card was counted SEEN in this session: scrolling back or coming back from a detail counts nothing. */
const seenThisSession = new Set<string>();

/** For tests: forget which cards were counted. */
export const resetSeenCards = (): void => seenThisSession.clear();

/** Half of a card on screen is a card seen. */
const SEEN_THRESHOLD = 0.5;

/**
 * Counts a card SEEN (POST …/engagements) the first time half of it is on screen, once per listing and session, and only
 * while PRO_LISTING_FEED is on: pro-core answers the route 404 otherwise. Returns the ref to put on each card. Without
 * an IntersectionObserver (old browsers, tests) nothing is counted; a failure is swallowed as every count's is.
 */
export const useCardSeen = (): ((
  refId: string
) => (element: Element | null) => void) => {
  const { isEnabled } = useListingFeedEnabled();
  const { mutate } = useRecordEngagementMutation();
  const observer = useRef<IntersectionObserver | null>(null);
  const elements = useRef(new Map<string, Element>());
  const callbacks = useRef(
    new Map<string, (element: Element | null) => void>()
  );

  useEffect(() => {
    if (!isEnabled || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const current = new IntersectionObserver(
      entries =>
        entries.forEach(entry => {
          // The first notification can come with a sliver on screen: only half a card is a card seen.
          if (
            !entry.isIntersecting ||
            entry.intersectionRatio < SEEN_THRESHOLD
          ) {
            return;
          }
          current.unobserve(entry.target);
          const refId = (entry.target as HTMLElement).dataset.listingRefId;
          if (!refId || seenThisSession.has(refId)) return;
          seenThisSession.add(refId);
          mutate(
            { refId, action: 'SEEN', source: 'feed' },
            { onError: () => undefined }
          );
        }),
      { threshold: SEEN_THRESHOLD }
    );
    observer.current = current;
    // Cards attach their ref before this effect runs, so the ones already there are observed now.
    elements.current.forEach((element, refId) => {
      if (!seenThisSession.has(refId)) current.observe(element);
    });
    return () => {
      current.disconnect();
      observer.current = null;
    };
  }, [isEnabled, mutate]);

  return useCallback((refId: string) => {
    let callback = callbacks.current.get(refId);
    if (!callback) {
      callback = (element: Element | null) => {
        const previous = elements.current.get(refId);
        if (previous) observer.current?.unobserve(previous);
        if (!element) {
          elements.current.delete(refId);
          return;
        }
        (element as HTMLElement).dataset.listingRefId = refId;
        elements.current.set(refId, element);
        if (!seenThisSession.has(refId)) observer.current?.observe(element);
      };
      callbacks.current.set(refId, callback);
    }
    return callback;
  }, []);
};

/**
 * Records a step of the apply funnel for one listing from one place in the app (`detail` for OPENED, `apply` for the
 * APPLY_* steps), each step once per listing, and only while PRO_LISTING_FEED is on:
 * pro-core has no route for it otherwise. A failure is swallowed: the talent loses nothing when a count misses one.
 */
export const useListingEngagement = (
  refId: string,
  source: ListingEngagementSource
): ((action: ListingEngagementAction) => void) => {
  const { isEnabled } = useListingFeedEnabled();
  const { mutate } = useRecordEngagementMutation();
  // The detail route keeps this instance when it moves to another listing, so the steps are kept per refId.
  const recorded = useRef({
    refId,
    actions: new Set<ListingEngagementAction>()
  });

  return useCallback(
    (action: ListingEngagementAction) => {
      if (recorded.current.refId !== refId) {
        recorded.current = { refId, actions: new Set() };
      }
      if (!isEnabled || recorded.current.actions.has(action)) return;
      recorded.current.actions.add(action);
      mutate({ refId, action, source }, { onError: () => undefined });
    },
    [isEnabled, mutate, refId, source]
  );
};

export interface WeekFitState {
  answer: WeekFitAnswer | undefined;
  /** The first answer is not there yet. */
  isLoading: boolean;
  /** An edited week is being checked; the last answer stays on screen meanwhile. */
  isChecking: boolean;
  isError: boolean;
  /** The code of the failed check; `error.job-listings.unavailable` is pro-core's apply v2 being off for this talent. */
  errorCode: string | null;
}

/** The apply flow's week check: the profile's week first, then every edit of a day asks again. */
export const useWeekFit = (
  refId: string,
  request: WeekFitRequest,
  enabled: boolean
): WeekFitState => {
  const { data, isLoading, isFetching, isError, error } = useWeekFitQuery(
    refId,
    request,
    enabled
  );
  return {
    answer: data,
    isLoading: enabled && isLoading,
    isChecking: isFetching,
    isError,
    errorCode: isError ? codeOf(error) || UNNAMED_ERROR_CODE : null
  };
};

export interface ApplyState {
  /** Posts once; a second call while one is in flight is ignored. Rejects with the ApiError so a caller may await it. */
  /** PJL: the apply flow passes the week it collected; without it, today's apply. */
  apply: (extras?: ApplyExtras) => Promise<ApplyAnswer>;
  isApplying: boolean;
  applicationRefId: string | undefined;
  /** 200 rather than 201: the opening was applied to already, through this or another wording. */
  wasAlreadyApplied: boolean;
  /** The code of a refusal, which the screen turns into copy; null while nothing failed; unnamed rather than null for a failure without one. */
  errorCode: string | null;
  reset: () => void;
}

export const useApplyToJobListing = (jobListingRefId: string): ApplyState => {
  const { mutateAsync, isLoading, data, error, reset } =
    useApplyToJobListingMutation(jobListingRefId);
  // The press in flight, so a second press joins it instead of posting a second application.
  const inFlight = useRef<Promise<ApplyAnswer> | null>(null);

  return {
    apply: extras => {
      if (inFlight.current) return inFlight.current;
      const request = mutateAsync(extras).finally(() => {
        inFlight.current = null;
      });
      inFlight.current = request;
      return request;
    },
    isApplying: isLoading,
    applicationRefId: data?.applicationRefId,
    wasAlreadyApplied: data ? !data.created : false,
    // An empty code is a failure without a name, not a missing failure.
    errorCode: error ? codeOf(error) || UNNAMED_ERROR_CODE : null,
    reset
  };
};
